import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../cn";

// One shared badge baseline (22px / 11px) so every chip in the library lines up.
const pill = cva(
  "inline-flex items-center gap-1.5 rounded-full font-medium leading-none whitespace-nowrap",
  {
    variants: {
      variant: {
        success: "bg-okSoft text-okStrong",
        info: "bg-infoSoft text-infoStrong",
        warn: "bg-warnSoft text-warnStrong",
        danger: "bg-badSoft text-badStrong",
        neutral: "bg-neutralSoft text-mute",
        brand: "bg-brandSoft text-brand",
        accentSoft: "bg-brandSoft text-brand",
        outline: "border border-line text-mute",
      },
      size: {
        md: "h-[22px] px-2 text-[11px]",
        sm: "h-[18px] px-1.5 text-[10px]",
      },
    },
    defaultVariants: { variant: "neutral", size: "md" },
  },
);

/** The five semantic statuses must always carry an indicator, never colour alone. */
const STATUS_VARIANTS = ["success", "info", "warn", "danger", "neutral"] as const;

export interface PillProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof pill> {
  /** Optional leading dot indicator. */
  dot?: boolean;
  /** Optional leading glyph — never rely on colour alone (accessibility). */
  icon?: React.ReactNode;
}

export function Pill({ className, variant, size, dot, icon, children, ...props }: PillProps) {
  // Structural pairing rule: a semantic status pill always shows colour + indicator
  // + text. If the caller gives neither an icon nor a dot, we fall back to a dot so a
  // colour-only status is impossible by construction (spec / brief §2.3).
  const isStatus = !!variant && (STATUS_VARIANTS as readonly string[]).includes(variant);
  const showDot = (dot ?? (isStatus && !icon)) && !icon;
  return (
    <span className={cn(pill({ variant, size }), className)} {...props}>
      {icon}
      {showDot && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />}
      {children}
    </span>
  );
}

/** Maps a GREEN/AMBER/RED alert state to a Pill variant + label. */
export function AlertPill({ state }: { state: "GREEN" | "AMBER" | "RED" }) {
  const map = {
    GREEN: { variant: "success", label: "Green" },
    AMBER: { variant: "warn", label: "Amber" },
    RED: { variant: "danger", label: "Red" },
  } as const;
  const m = map[state];
  return (
    <Pill variant={m.variant} dot>
      {m.label}
    </Pill>
  );
}
